import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  CheckCircle2,
  Shield,
  Hammer,
  Wrench,
  CloudLightning,
  MapPin,
} from "lucide-react";
import { SEOHead } from "@/components/SEOHead";
import { CityServicesSection } from "@/components/CrossLinks";

export default function PrinceAlbert() {
  const faqs = [
    {
      "@type": "Question",
      name: "Do I need a permit for a roof replacement in Prince Albert?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Generally, a standard residential roof replacement does not require a building permit in Prince Albert. However, if structural repairs to the trusses or decking are needed, a permit may be required. We handle all necessary assessments and compliance.",
      },
    },
    {
      "@type": "Question",
      name: "How do you handle winter roof replacements in Prince Albert?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "While summer is peak season, we can perform emergency repairs and certain installations during winter in Prince Albert using specialized techniques and materials suited for cold weather application.",
      },
    },
    {
      "@type": "Question",
      name: "What roofing material handles northern Saskatchewan snow load best?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Premium metal roofing is excellent for shedding snow, while high-quality architectural asphalt shingles with proper ice and water shield offer great durability against heavy snow loads and freeze-thaw cycles.",
      },
    },
  ];

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://www.saskroofingcompany.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Service Areas",
          item: "https://www.saskroofingcompany.com/areas",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "Prince Albert",
          item: "https://www.saskroofingcompany.com/areas/prince-albert",
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "RoofingContractor",
      "@id": "https://www.saskroofingcompany.com/#localbusiness",
      name: "Sask Roofing Company",
      url: "https://www.saskroofingcompany.com/",
      telephone: "+1-639-739-7969",
      email: "info@saskroofingcompany.com",
      areaServed: {
        "@type": "City",
        name: "Prince Albert",
        containedInPlace: { "@type": "State", name: "Saskatchewan" },
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs,
    },
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <SEOHead
        title="Roofing Company Prince Albert | Sask Roofing Company"
        description="Looking for a reliable roofing company in Prince Albert? We provide expert roof repair, replacement, and storm damage restoration for Prince Albert homes."
        canonical="/areas/prince-albert"
        schemaJson={schema}
      />

      {/* Hero */}
      <section className="relative py-32 text-primary-foreground overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/b963df3a-8576-4537-8ff4-9d7e9bcdc2c5.png&output=webp&q=60"
            alt="Completed residential roof in Prince Albert, Saskatchewan"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
        </div>
        <div className="container relative px-4 z-10">
          <div className="max-w-3xl space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white drop-shadow-md">
              Roofing Company Prince Albert
            </h1>
            <p className="text-xl text-white/90 drop-shadow-sm font-medium">
              When you need a reliable roofing company near you in Prince
              Albert, we deliver honest pricing, premium materials, and expert
              installation built for the northern climate. From heavy snow loads
              to freezing temperatures, we know what it takes to protect your
              home.
            </p>
            <div className="pt-4 flex flex-wrap gap-4">
              <Button
                size="lg"
                className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-lg"
                asChild
              >
                <Link to="/contact">Get Your Free Roof Inspection</Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="bg-transparent text-white border-white hover:bg-white hover:text-primary shadow-lg"
                asChild
              >
                <a href="tel:6397397969">Call Us (639) 739-7969</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stat Callout Grid */}
      <section className="py-12 bg-background border-b">
        <div className="container px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex flex-col items-center text-center p-6 bg-muted/30 rounded-xl border">
              <span className="text-4xl font-bold text-primary mb-2">200+</span>
              <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Prince Albert Roofs
              </span>
            </div>
            <div className="flex flex-col items-center text-center p-6 bg-muted/30 rounded-xl border">
              <span className="text-4xl font-bold text-primary mb-2">
                24-48h
              </span>
              <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Avg. Response Time
              </span>
            </div>
            <div className="flex flex-col items-center text-center p-6 bg-muted/30 rounded-xl border">
              <span className="text-4xl font-bold text-primary mb-2">15+</span>
              <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Years Serving SK
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* City Context Card & Built for Climate */}
      <section className="py-20 bg-background">
        <div className="container px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent font-medium text-sm">
                <MapPin className="w-4 h-4" />
                Serving Prince Albert & Surrounding Areas
              </div>
              <h2 className="text-3xl md:text-4xl font-bold">
                Built for Prince Albert's Climate
              </h2>

              {/* City Context Card */}
              <div className="p-6 bg-muted/50 border-l-4 border-primary rounded-r-xl">
                <p className="font-medium text-foreground">
                  Prince Albert experiences long, bitterly cold winters with
                  significant snow accumulation. This heavy snow load combined
                  with freeze-thaw cycles makes proper attic ventilation and
                  extensive ice-and-water shield coverage absolutely critical to
                  prevent destructive ice damming.
                </p>
              </div>

              <p className="text-lg text-muted-foreground">
                Prince Albert homeowners face unique roofing challenges compared
                to the southern prairies. The "Gateway to the North" requires a
                roofing system that can handle sustained deep freezes, heavy
                snowpack, and the rapid melting of spring. As an experienced
                roofing contractor serving Prince Albert, we don't just nail
                down shingles; we engineer roofs that work with the climate, not
                against it. Whether you're in a mature neighborhood like
                Crescent Heights dealing with complex rooflines and older
                ventilation, or a newer build in the Southwood area, our crews
                know exactly how to protect your home from the top down.
              </p>
              <ul className="space-y-4 pt-4">
                {[
                  "Extensive ice and water shield for winter protection",
                  "Proper ventilation engineering to prevent ice damming",
                  "High-quality underlayments that remain flexible in extreme cold",
                  "Impact-resistant shingles for summer hail protection",
                ].map((item, i) => (
                  <li key={i} className="flex gap-3 text-muted-foreground">
                    <CheckCircle2 className="w-6 h-6 text-primary shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl h-[500px]">
              <img
                src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/f1796618-613f-4a0c-8e31-90786ba9c4af.png&output=webp&q=60"
                alt="Roofing crew working on a home in Prince Albert"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-muted/30 border-y">
        <div className="container px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-bold mb-4">
              Our Prince Albert Roofing Services
            </h2>
            <p className="text-lg text-muted-foreground">
              We offer a complete range of roofing services tailored for the
              northern climate. From emergency winter leak repairs to full
              summer replacements, our teams are equipped to handle the specific
              needs of Prince Albert homes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link
              to="/areas/prince-albert/replacement"
              className="group bg-card p-6 rounded-xl border shadow-sm hover:shadow-md transition-all"
            >
              <Shield className="w-12 h-12 text-primary mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold mb-2">Roof Replacement</h3>
              <p className="text-muted-foreground text-sm">
                Complete tear-offs and expert installations of new,
                climate-tested roofing systems.
              </p>
            </Link>

            <Link
              to="/areas/prince-albert/repair"
              className="group bg-card p-6 rounded-xl border shadow-sm hover:shadow-md transition-all"
            >
              <Wrench className="w-12 h-12 text-primary mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold mb-2">Roof Repair</h3>
              <p className="text-muted-foreground text-sm">
                Fast fixes for ice damage, missing shingles, flashing leaks, and
                general wear and tear.
              </p>
            </Link>

            <Link
              to="/services/storm-damage"
              className="group bg-card p-6 rounded-xl border shadow-sm hover:shadow-md transition-all"
            >
              <CloudLightning className="w-12 h-12 text-primary mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold mb-2">Storm Restoration</h3>
              <p className="text-muted-foreground text-sm">
                Emergency tarping and expert insurance claim assistance
                following severe weather events.
              </p>
            </Link>

            <Link
              to="/services/inspections"
              className="group bg-card p-6 rounded-xl border shadow-sm hover:shadow-md transition-all"
            >
              <Hammer className="w-12 h-12 text-primary mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold mb-2">Roof Inspections</h3>
              <p className="text-muted-foreground text-sm">
                Thorough assessments of your roof's health, focusing on
                ventilation and winter-readiness.
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* Cost Context & Comparison Table */}
      <section className="py-20 bg-background">
        <div className="container px-4 max-w-5xl">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold mb-4">
              What Does a New Roof Cost in Prince Albert?
            </h2>
            <p className="text-lg text-muted-foreground">
              We provide upfront, honest pricing for all Prince Albert
              homeowners. Your exact cost is influenced by the square footage,
              steepness, and the specific shingle or metal option you choose.
              Our quotes are comprehensive, meaning the price you sign for is
              the price you pay.
            </p>
          </div>

          <div className="overflow-x-auto rounded-xl border shadow-sm">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-muted/50">
                  <th className="p-4 font-bold border-b">Service Type</th>
                  <th className="p-4 font-bold border-b">Estimated Cost</th>
                  <th className="p-4 font-bold border-b">Best For</th>
                  <th className="p-4 font-bold border-b">Typical Timeline</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr className="hover:bg-muted/20 transition-colors">
                  <td className="p-4 font-medium">
                    Asphalt Shingle Replacement
                  </td>
                  <td className="p-4 text-muted-foreground">
                    $6,000 - $12,000+
                  </td>
                  <td className="p-4 text-muted-foreground">
                    Most residential homes, budget-friendly durability
                  </td>
                  <td className="p-4 text-muted-foreground">1-2 Days</td>
                </tr>
                <tr className="hover:bg-muted/20 transition-colors">
                  <td className="p-4 font-medium">Metal Roof Installation</td>
                  <td className="p-4 text-muted-foreground">
                    $12,000 - $25,000+
                  </td>
                  <td className="p-4 text-muted-foreground">
                    Maximum lifespan, ultimate hail/snow resistance
                  </td>
                  <td className="p-4 text-muted-foreground">3-5 Days</td>
                </tr>
                <tr className="hover:bg-muted/20 transition-colors">
                  <td className="p-4 font-medium">Targeted Roof Repair</td>
                  <td className="p-4 text-muted-foreground">$400 - $1,500</td>
                  <td className="p-4 text-muted-foreground">
                    Fixing isolated leaks, blown-off shingles, or flashing
                  </td>
                  <td className="p-4 text-muted-foreground">2-4 Hours</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-sm text-muted-foreground text-center mt-4">
            * These are rough estimates based on average Prince Albert home
            sizes. Contact us for a precise, fixed-price quote.
          </p>
        </div>
      </section>

      {/* All Services Grid */}
      <CityServicesSection city="Prince Albert" citySlug="prince-albert" />

      {/* FAQ */}
      <section className="py-20 bg-muted/30 border-t">
        <div className="container px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-center mb-12">
            Prince Albert Roofing FAQ
          </h2>
          <Accordion
            type="single"
            collapsible
            className="w-full bg-card rounded-xl border p-4 shadow-sm"
          >
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`item-${i}`}>
                <AccordionTrigger className="text-lg font-medium text-left">
                  {faq.name}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base">
                  {faq.acceptedAnswer.text}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 bg-primary text-primary-foreground text-center">
        <div className="container px-4 max-w-3xl space-y-8">
          <h2 className="text-3xl md:text-4xl font-bold">
            Ready to Upgrade Your Prince Albert Roof?
          </h2>
          <p className="text-xl text-primary-foreground/80">
            Reach out today for a free, no-obligation inspection and transparent
            quote.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button
              size="lg"
              className="bg-accent text-accent-foreground hover:bg-accent/90"
              asChild
            >
              <Link to="/contact">Request a Free Quote</Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="bg-transparent text-primary-foreground border-primary-foreground hover:bg-primary-foreground hover:text-primary"
              asChild
            >
              <a href="tel:6397397969">Call Now (639) 739-7969</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
